import React, { useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import DynamicForm from 'components/FormSubmission/dynamicForm';
import axios from 'axios';
import TailwindLayout from 'TailwindcssComponents/TailwindLayout';
import TopBar from 'TailwindcssComponents/TopBar';
import FooterTailwind from 'TailwindcssComponents/Footer';
import SeatBookingModel from 'pages/Models/seatBookingModel';

export default function TicketBookingAndRegistration() {
    const apiUrl = process.env.REACT_APP_API_BASE_URL;
    const { eventId } = useParams();
    const location = useLocation();
    const eventDetails = location.state || {};

    const {
        eventName, venueName, date, time, isFree,
        bookType: isSingleRegistrationPerTicket,
        purchaseLimit: ticketPurchaseLimit, orgId, bookedBy
    } = eventDetails;

    const [eventZoneData, setEventZoneData] = useState([]);
    const [availableTicketsMap, setAvailableTicketsMap] = useState({});
    const [selectedZoneId, setSelectedZoneId] = useState(null);
    const [selectedCategoryId, setSelectedCategoryId] = useState(null);
    const [ticketsCount, setTicketsCount] = useState(1);
    const [formFieldData, setFormFieldData] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [seatViewModelOpen, setSeatViewModelOpen] = useState(false);
    const [choosenSeatsData, setChoosenSeatsData] = useState([]);

    const fetchEventZones = async () => {
        try {
            const [zoneRes, categoryRes] = await Promise.all([
                axios.get(`${apiUrl}event-service/event-zone`, { params: { event: eventId } }),
                axios.get(`${apiUrl}event-service/ticket-category`, { params: { event: eventId } })
            ]);

            const zones = zoneRes.data?.data || [];
            const categories = categoryRes.data?.success ? categoryRes.data.data : [];

            const categoriesByZone = categories.reduce((acc, category) => {
                (acc[category.zoneId] = acc[category.zoneId] || []).push(category);
                return acc;
            }, {});

            const enrichedZones = zones.map(zone => ({
                ...zone,
                ticketCategories: categoriesByZone[zone.id] || [],
            }));

            setEventZoneData(enrichedZones);
        } catch (error) {
            console.error("Error fetching data:", error);
            toast.error("Failed to fetch event data.");
        }
    };

    const fetchRegistrationFormField = async () => {
        try {
            const res = await axios.get(`${apiUrl}event-service/registration-form`, {
                params: { event: eventId },
            });
            if (res.data?.success) setFormFieldData(res.data.data);
        } catch (error) {
            console.error("Error fetching form fields:", error);
        }
    };

    useEffect(() => {
        fetchEventZones();
        fetchRegistrationFormField();
    }, [eventId]);

    const selectedZoneTicketCategories = useMemo(() => {
        return eventZoneData.find(z => z.id === selectedZoneId)?.ticketCategories || [];
    }, [eventZoneData, selectedZoneId]);

    const selectedCategory = useMemo(() => {
        return selectedZoneTicketCategories.find(c => c.id === selectedCategoryId) || {};
    }, [selectedZoneTicketCategories, selectedCategoryId]);

    useEffect(() => {
        const allCategories = eventZoneData.flatMap(zone => zone.ticketCategories);
        const ticketsMap = allCategories.reduce((acc, cat) => {
            acc[cat.id] = cat.totalCount - cat.soldTickets;
            return acc;
        }, {});
        setAvailableTicketsMap(ticketsMap);
    }, [eventZoneData]);

    const isAllowedToBook = (availableTicketsMap[selectedCategoryId] || 0) > 0;

    return (
        <TailwindLayout>
            <TopBar />
            <div className="flex justify-center items-center min-h-screen p-6">
                <div className="w-full max-w-2xl mx-auto px-4">
                    <div className="bg-white rounded-lg shadow-lg p-6 md:p-8 mb-8">
                        <h2 className="text-xl font-medium mb-4">Register For The Event - {eventName}</h2>

                        <div className="grid grid-cols-1 gap-4 mb-2">
                            {/* Zone Selection */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Select Zone</label>
                                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 mt-2">
                                    {eventZoneData.length ? (
                                        eventZoneData.map(zone => (
                                            <div key={zone.id} className="flex items-center space-x-2 p-2 border rounded-md hover:bg-gray-50 cursor-pointer">
                                                <input
                                                    type="radio"
                                                    id={`zone-${zone.id}`}
                                                    name="eventZone"
                                                    value={zone.id}
                                                    checked={selectedZoneId === zone.id}
                                                    onChange={() => {
                                                        setSelectedZoneId(zone.id);
                                                        setSelectedCategoryId(null);
                                                    }}
                                                    className="h-4 w-4 border-gray-300 text-blue-600 focus:ring-blue-500"
                                                />
                                                <label htmlFor={`zone-${zone.id}`} className="ml-2 text-sm text-gray-700">
                                                    {zone.name}
                                                </label>
                                            </div>
                                        ))
                                    ) : <p className="text-gray-500">No zones available</p>}
                                </div>
                            </div>

                            {/* Ticket Categories */}
                            {selectedZoneId && (
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Ticket Category</label>
                                    {selectedZoneTicketCategories.length ? (
                                        selectedZoneTicketCategories.map(category => (
                                            <div key={category.id} className="flex items-center">
                                                <input
                                                    type="radio"
                                                    id={`category-${category.id}`}
                                                    name="ticketCategory"
                                                    value={category.id}
                                                    checked={selectedCategoryId === category.id}
                                                    onChange={() => setSelectedCategoryId(category.id)}
                                                    className="h-4 w-4 border-gray-300 text-blue-600 focus:ring-blue-500"
                                                />
                                                <label htmlFor={`category-${category.id}`} className="ml-2 text-sm text-gray-700">
                                                    {category.name} ({availableTicketsMap[category.id] || 0} available)
                                                </label>
                                            </div>
                                        ))
                                    ) : <p className="text-gray-500">No ticket categories available</p>}
                                </div>
                            )}

                            {/* Ticket Count */}
                            {selectedCategoryId && (
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Tickets Count</label>
                                    <input
                                        type="number"
                                        value={ticketsCount}
                                        onChange={(e) => {
                                            const value = Math.max(1, Math.min(Number(e.target.value), availableTicketsMap[selectedCategoryId] || 1));
                                            setTicketsCount(value);
                                        }}
                                        min="1"
                                        max={availableTicketsMap[selectedCategoryId] || 1}
                                        disabled={!isAllowedToBook}
                                        className="w-full h-[45px] rounded-md border px-3 py-2 text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                                    />
                                </div>
                            )}
                        </div>

                        {/* Seat Booking */}
                        {selectedZoneId && eventZoneData.find(z => z.id === selectedZoneId)?.isSeat && (
                            <button
                                className="mt-2 px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600"
                                onClick={() => setSeatViewModelOpen(true)}
                            >
                                Select Seats
                            </button>
                        )}

                        <SeatBookingModel
                            open={seatViewModelOpen}
                            onClose={() => setSeatViewModelOpen(false)}
                            seatsCount={ticketsCount}
                            seatData={eventZoneData.find(z => z.id === selectedZoneId)?.seats || []}
                            selectedSeatsData={setChoosenSeatsData}
                        />

                        {/* Registration Form */}
                        <div className="w-full mt-6">
                            {isLoading ? (
                                <div className="flex justify-center">
                                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                                </div>
                            ) : (
                                <DynamicForm
                                    className="m-4"
                                    fields={formFieldData}
                                    eventId={eventId}
                                    eventName={eventName}
                                    venueName={venueName}
                                    date={date}
                                    time={time}
                                    categoryId={selectedCategoryId}
                                    categoryName={selectedCategory.name || ""}
                                    price={selectedCategory.price || 0}
                                    bookedBy={bookedBy}
                                    isAllowedToBook={isAllowedToBook}
                                    isSingleRegistrationPerTicket={isSingleRegistrationPerTicket}
                                    ticketPurchaseLimit={ticketPurchaseLimit}
                                    eventOrganizationId={orgId}
                                    ticketsCount={ticketsCount}
                                    seats={choosenSeatsData}
                                    isSeat={eventZoneData.find(z => z.id === selectedZoneId)?.isSeat || false}
                                />
                            )}
                        </div>
                    </div>
                </div>
            </div>
            <FooterTailwind />
        </TailwindLayout>
    );
}
